// Shared foundation for both folder-upload entry points (native OS drag-drop and the
// `webkitdirectory` picker input): both converge on this same normalized shape so the upload
// orchestrator (uploadFolderTree.ts) never needs to know which entry point produced its input.
export interface FlattenedUploadEntry {
  relativePath: string;
  file: File;
}

const joinRelativePath = (prefix: string, name: string): string =>
  prefix ? `${prefix}/${name}` : name;

const readFileFromEntry = (entry: FileSystemFileEntry): Promise<File> =>
  new Promise((resolve, reject) => entry.file(resolve, reject));

// readEntries() is explicitly NOT guaranteed to return every child in one call - the File and
// Directory Entries API spec requires calling it repeatedly until it returns an empty array.
const readAllDirectoryEntries = async (
  dirEntry: FileSystemDirectoryEntry,
): Promise<FileSystemEntry[]> => {
  const reader = dirEntry.createReader();
  const readBatch = (): Promise<FileSystemEntry[]> =>
    new Promise((resolve, reject) => reader.readEntries(resolve, reject));

  const all: FileSystemEntry[] = [];
  let batch = await readBatch();
  while (batch.length > 0) {
    all.push(...batch);
    batch = await readBatch();
  }
  return all;
};

const flattenEntry = async (
  entry: FileSystemEntry,
  prefix: string,
): Promise<FlattenedUploadEntry[]> => {
  if (entry.isFile) {
    const file = await readFileFromEntry(entry as FileSystemFileEntry);
    return [{ relativePath: joinRelativePath(prefix, entry.name), file }];
  }

  const childEntries = await readAllDirectoryEntries(entry as FileSystemDirectoryEntry);
  const nestedPrefix = joinRelativePath(prefix, entry.name);
  const nested = await Promise.all(
    childEntries.map((child) => flattenEntry(child, nestedPrefix)),
  );
  return nested.flat();
};

/** Walks dropped `DataTransferItem`s (native OS drag-drop) into a flat, path-prefixed file list. */
export async function flattenDataTransferItems(
  items: DataTransferItem[],
): Promise<FlattenedUploadEntry[]> {
  const fileItems = items.filter((item) => item.kind === 'file');

  const results = await Promise.all(
    fileItems.map(async (item): Promise<FlattenedUploadEntry[]> => {
      const entry = item.webkitGetAsEntry?.();
      if (!entry) {
        // Entries API unavailable/unsupported: fall back to treating the item as a flat file.
        const file = item.getAsFile();
        return file ? [{ relativePath: file.name, file }] : [];
      }
      return flattenEntry(entry, '');
    }),
  );

  return results.flat();
}

/** Normalizes a `FileList` from a `webkitdirectory` picker input into the same shape. */
export function flattenFileList(files: FileList | File[]): FlattenedUploadEntry[] {
  return Array.from(files).map((file) => ({
    relativePath: file.webkitRelativePath || file.name,
    file,
  }));
}
